import { useEffect, useState } from 'react'
import { debug, logError } from 'lib/logging'

/**
 * Rows for a scrolling table, plus the scroll handler that asks for the next page.
 *
 * `loadMore` comes from the caller and is expected to append onto the same list that
 * produced `initialData` (a store's `fetchNextPage`), so `initialData` grows and the
 * rows here follow it. Without a `loadMore` the table simply stays on its first page:
 * this used to re-fetch the current route URL for `?page=N`, which returned the SPA's
 * index.html once the app stopped being served by a Remix loader, so `.json()` rejected
 * and no row was ever appended.
 */
export function useInfiniteScroll<T>(
  initialData: T[] | undefined,
  total?: number,
  loadMore?: () => Promise<unknown>,
): [T[], boolean, (event: React.UIEvent<HTMLDivElement>) => void] {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<T[]>(initialData || [])

  useEffect(() => {
    setItems(initialData || [])
  }, [initialData])

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    if (!loadMore || !total || loading) return
    if (scrollTop + clientHeight < scrollHeight - 100) return
    if (items.length >= total) return
    debug(`table`, `fetching next page`, items.length, total)
    setLoading(true)
    loadMore()
      .catch((err) => logError(`Error loading next page`, err))
      .finally(() => setLoading(false))
  }

  return [items, loading, onScroll]
}
